<div class="model-selector">
  <div *ngIf="loading" class="ms-status">
    <nb-icon icon="loader-outline"></nb-icon>
    <span>{{ 'pages.tutors.modelSelector.loading' | translate }}</span>
  </div>

  <div *ngIf="error && !loading" class="ms-status ms-status--error">
    {{ 'pages.tutors.modelSelector.error' | translate }}
  </div>

  <div *ngIf="!loading && !error" class="ms">
    <!-- Segment tabs -->
    <div class="ms-tabs" role="tablist">
      <button
        type="button"
        *ngFor="let seg of segments"
        class="ms-tab"
        role="tab"
        [class.ms-tab--active]="activeSegment === seg"
        [attr.aria-selected]="activeSegment === seg"
        [disabled]="segmentCount(seg) === 0"
        (click)="activeSegment = seg"
      >
        {{ segmentLabel(seg) }}
        <span class="ms-tab__count">{{ segmentCount(seg) }}</span>
      </button>
    </div>

    <!-- One tile per model; a speed variant of the same model is a switch
         on that tile, not a tile of its own. Vendors grouped, a vendor's
         models from the strongest down. -->
    <div class="ms-list" role="radiogroup">
      <button
        type="button"
        *ngFor="let t of tilesIn(activeSegment); trackBy: trackTile"
        class="ms-opt"
        role="radio"
        [class.ms-opt--selected]="isTileSelected(t)"
        [attr.aria-checked]="isTileSelected(t)"
        [disabled]="saving"
        (click)="selectTile(t)"
      >
        <ng-container *ngIf="active(t) as p">
          <span class="ms-logo">
            <img
              *ngIf="hasLogo(t.base)"
              class="ms-logo__img"
              [src]="logoSrc(t.base)"
              [alt]="providerName(t.base)"
            />
            <span
              *ngIf="!hasLogo(t.base)"
              class="ms-logo__initial"
              [style.color]="brandColor(t.base)"
              >{{ brandInitial(t.base) }}</span
            >
          </span>

          <span class="ms-opt__body">
            <span class="ms-opt__name">{{ providerName(t.base) }}</span>
            <span class="ms-opt__desc" *ngIf="providerDesc(t.base)">{{
              providerDesc(t.base)
            }}</span>
            <code class="ms-opt__model">{{ t.base.model_label || t.base.model }}</code>

            <!-- Speed switch: the same model on a faster service tier -->
            <span
              *ngIf="t.fast as f"
              class="ms-fast"
              role="switch"
              tabindex="0"
              [class.ms-fast--on]="fastOn(t)"
              [class.ms-fast--ultra]="f.speed === 'ultrafast'"
              [attr.aria-checked]="fastOn(t)"
              [nbTooltip]="speedHint(f)"
              nbTooltipPlacement="top"
              (click)="toggleFast(t, $event)"
              (keydown.enter)="toggleFast(t, $event)"
              (keydown.space)="toggleFast(t, $event)"
            >
              <span class="ms-fast__track"><span class="ms-fast__thumb"></span></span>
              <nb-icon icon="flash-outline"></nb-icon>
              {{ speedLabel(f) }}
              <span class="ms-fast__mult">{{ f.credit_multiplier }}×</span>
            </span>

            <!-- A model that is fast by itself (no slower sibling) -->
            <span
              *ngIf="!t.fast && t.base.speed"
              class="ms-speed"
              [ngClass]="'ms-speed--' + t.base.speed"
              [nbTooltip]="speedHint(t.base)"
              nbTooltipPlacement="top"
              ><nb-icon icon="flash-outline"></nb-icon>{{ speedLabel(t.base) }}</span
            >
            <span
              *ngIf="showAgentInfo && agentStatus(p)"
              class="ms-agent"
              [ngClass]="'ms-agent--' + agentStatusKey(p)"
              [nbTooltip]="agentTooltip(p)"
              nbTooltipPlacement="top"
              >{{ agentLabel(p) }}</span
            >
          </span>

          <span class="ms-opt__meta">
            <nb-icon
              *ngIf="isTileSelected(t)"
              class="ms-opt__check"
              icon="checkmark-circle-2"
            ></nb-icon>
            <span class="ms-badge" [ngClass]="badgeClass(p)"
              >{{ p.credit_multiplier }}×</span
            >
            <nb-icon
              class="ms-opt__info"
              icon="info-outline"
              [nbTooltip]="creditTooltip(p)"
              nbTooltipPlacement="left"
              (click)="$event.stopPropagation()"
            ></nb-icon>
          </span>
        </ng-container>
      </button>

      <p *ngIf="tilesIn(activeSegment).length === 0" class="ms-empty">
        {{ 'pages.tutors.modelSelector.emptySegment' | translate }}
      </p>
    </div>

    <p class="ms-speed-hint" *ngIf="selectedProvider?.speed as sp">
      <nb-icon icon="flash-outline"></nb-icon>{{ speedHint(selectedProvider!) }}
    </p>

    <!-- Agent hints for the selected model (weak spots × this agent's
         configuration). Only where the loop runs on it, or for super_admins. -->
    <ul class="ms-agent-hints" *ngIf="selectedHints.length">
      <li *ngFor="let h of selectedHints">
        {{ h.key | translate: h.params }}
      </li>
    </ul>

    <!-- super_admin: per-agent opt-in while the global switch is opt_in -->
    <div class="ms-loop" *ngIf="isSuperAdmin && loopFlagSetting && agentLoopMode === 'opt_in'">
      <nb-toggle
        [checked]="loopFlagSetting.value === true || loopFlagSetting.value === 'true'"
        [disabled]="savingLoopFlag"
        (checkedChange)="toggleAgentLoop($event)"
        labelPosition="end"
        >{{ 'pages.tutors.modelSelector.agent.loopToggle' | translate }}</nb-toggle
      >
      <p class="ms-loop__hint">
        {{ 'pages.tutors.modelSelector.agent.loopMode.' + agentLoopMode | translate }}
      </p>
    </div>

    <!-- Credit reference table (all segments, active row highlighted) -->
    <div class="ms-table" *ngIf="tiers.length">
      <div class="ms-table__title">
        {{ 'pages.tutors.modelSelector.creditTable.title' | translate }}
      </div>
      <div
        class="ms-table__row"
        *ngFor="let t of tiers"
        [class.ms-table__row--active]="t.segment === activeSegment"
      >
        <span class="ms-table__cat">{{ segmentLabel(t.segment) }}</span>
        <span class="ms-badge" [ngClass]="'ms-badge--' + t.segment"
          >{{ tierFactor(t) }}×</span
        >
      </div>
    </div>

    <p class="ms-hint">{{ 'pages.tutors.modelSelector.creditHint' | translate }}</p>
    <p class="ms-updated" *ngIf="lastUpdated">
      {{ 'pages.tutors.modelSelector.lastUpdated' | translate: { date: lastUpdated } }}
    </p>
  </div>
</div>
